// Timezone math for group call posts. A coach schedules a call in a chosen
// timezone (say 6pm New York); we store the resulting absolute moment in UTC
// milliseconds plus the chosen zone's name, so every member's phone can show the
// call in whichever zone makes sense to them while everyone agrees on the moment.

const DEFAULT_TIME_ZONE = 'UTC'
const formatterCache = {}

export function getDeviceTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIME_ZONE
  } catch (error) {
    return DEFAULT_TIME_ZONE
  }
}

export function isValidTimeZone(timeZone) {
  if (!timeZone || typeof timeZone !== 'string') return false

  try {
    new Intl.DateTimeFormat('en-US', { timeZone }).format(new Date())
    return true
  } catch (error) {
    return false
  }
}

function getFormatter(timeZone) {
  if (!formatterCache[timeZone]) {
    formatterCache[timeZone] = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    })
  }

  return formatterCache[timeZone]
}

function getTimeZoneParts(timestamp, timeZone) {
  const parts = getFormatter(timeZone).formatToParts(new Date(timestamp))
  const value = (type) => Number(parts.find((part) => part.type === type)?.value)

  return {
    year: value('year'),
    month: value('month'),
    day: value('day'),
    hour: value('hour'),
    minute: value('minute'),
    second: value('second'),
  }
}

function getTimeZoneOffsetMs(timestamp, timeZone) {
  const parts = getTimeZoneParts(timestamp, timeZone)
  const zonedAsUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second)

  return zonedAsUtc - timestamp
}

function getDatePickerParts(date) {
  const d = new Date(date)

  return {
    year: d.getFullYear(),
    month: d.getMonth() + 1,
    day: d.getDate(),
    hour: d.getHours(),
    minute: d.getMinutes(),
    second: 0,
  }
}

function isSameWallTime(parts, target) {
  return (
    parts.year === target.year &&
    parts.month === target.month &&
    parts.day === target.day &&
    parts.hour === target.hour &&
    parts.minute === target.minute
  )
}

export function zonedDateTimeToUtcMs(date, timeZone) {
  if (!date || Number.isNaN(new Date(date).getTime())) {
    throw new Error('Invalid date')
  }
  if (!isValidTimeZone(timeZone)) {
    throw new Error('Invalid timezone')
  }

  const target = getDatePickerParts(date)
  const wallUtc = Date.UTC(target.year, target.month - 1, target.day, target.hour, target.minute, 0, 0)
  // The zone's offset can differ on either side of a DST transition, so probe the day before/after too
  // and keep whichever candidate round-trips back to the same wall-clock time.
  const candidateOffsets = [
    getTimeZoneOffsetMs(wallUtc, timeZone),
    getTimeZoneOffsetMs(wallUtc - 24 * 60 * 60 * 1000, timeZone),
    getTimeZoneOffsetMs(wallUtc + 24 * 60 * 60 * 1000, timeZone),
  ]
  const candidates = Array.from(new Set(candidateOffsets.map((offset) => wallUtc - offset))).sort((a, b) => a - b)
  const match = candidates.find((candidate) => isSameWallTime(getTimeZoneParts(candidate, timeZone), target))

  if (match === undefined) {
    throw new Error('Selected time does not exist in timezone')
  }

  return match
}

export function utcMsToDatePickerDate(timestamp, timeZone) {
  if (!timestamp && timestamp !== 0) return null
  if (!isValidTimeZone(timeZone)) return null

  const parts = getTimeZoneParts(Number(timestamp), timeZone)
  return new Date(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, 0, 0)
}

// The pieces of a moment as read in a given timezone, so the text below can be
// assembled by hand instead of taking whatever Intl prints.
//
// The time is parsed out of a formatted string rather than read from
// formatToParts, matching the mobile app: its JS engine returns date parts but no
// hour, minute or dayPeriod parts. The separator before AM/PM is a narrow
// no-break space on some platforms, which \s covers.
function getWindowParts(timestamp, timeZone) {
  const date = new Date(timestamp)
  const dateText = new Intl.DateTimeFormat('en-US', { timeZone, month: 'short', day: 'numeric' }).format(date)
  const year = new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric' }).format(date)
  const timeText = new Intl.DateTimeFormat('en-US', { timeZone, hour: 'numeric', minute: '2-digit' }).format(date)
  const match = timeText.match(/(\d{1,2}):(\d{2})\s*([AP]\.?M\.?)?/i)

  return {
    dateText,
    year,
    // Anything unparseable falls back to the whole formatted time, so a title is
    // never left with an empty hour.
    hour: match ? match[1] : timeText,
    minute: match ? match[2] : '00',
    meridiem: match?.[3] ? match[3].replace(/\./g, '').toUpperCase() : '',
  }
}

// "6 PM", "6:30 PM", or "6" when the AM/PM is dropped because both ends of the
// call share it.
function formatTime(parts, withMeridiem) {
  const time = `${parts.hour}${parts.minute === '00' ? '' : `:${parts.minute}`}`
  return withMeridiem && parts.meridiem ? `${time} ${parts.meridiem}` : time
}

// "Mar 4, 6 - 7 PM". Kept as short as the facts allow, because it is the title of
// a narrow card: on-the-hour times lose their ":00", the start loses its AM/PM
// when it matches the end's, and the year only appears when the call is not in
// the current year. A call that runs past midnight repeats the date
// ("Mar 4, 11 PM - Mar 5, 1 AM"). MIRROR of the mobile app's version.
//
// Shown in the coach's chosen timezone by default so the dashboard matches what
// the coach typed. The zone's name is not printed — the coach picked it, and it
// pushed the card's title onto a second line.
export function formatGroupCallWindow(post, displayTimeZone = post?.callTimeZone, currentTime = Date.now()) {
  const { callStartAt, callEndAt } = post || {}
  const start = Number(callStartAt)
  const end = Number(callEndAt)
  const timeZone = isValidTimeZone(displayTimeZone) ? displayTimeZone : getDeviceTimeZone()

  if (!start || !end) return ''

  const startParts = getWindowParts(start, timeZone)
  const endParts = getWindowParts(end, timeZone)
  const nowYear = getWindowParts(currentTime, timeZone).year
  const formatDate = (parts) => `${parts.dateText}${parts.year === nowYear ? '' : `, ${parts.year}`}`

  const startDate = formatDate(startParts)
  const sameDay = startParts.dateText === endParts.dateText && startParts.year === endParts.year

  if (sameDay) {
    const startText = formatTime(startParts, startParts.meridiem !== endParts.meridiem)
    return `${startDate}, ${startText} - ${formatTime(endParts, true)}`
  }

  const endDate = formatDate(endParts)
  return `${startDate}, ${formatTime(startParts, true)} - ${endDate}, ${formatTime(endParts, true)}`
}
